/**
 * Builders for the events a capture or an edit produces.
 *
 * Every one stamps the envelope from the persisted device clock and then parses
 * the result with the shared schema, so a malformed event fails here -- at the
 * line that built it -- rather than in the outbox, in the push response, or on
 * another device three days later.
 */
import {
  anyEvent,
  findTagByName,
  itemFieldNames,
  normalizeTagName,
  uuidv7,
  type AnyEvent,
  type ItemFieldName,
  type CustomFieldType,
  type FieldValue,
  type ItemFieldValues,
  type Manifest,
  type Money,
  type ReleaseKind,
} from '@intentionalist/shared';
import { deviceId, tick } from './clock.js';

function envelope(type: string, entityId: string, payload: unknown): AnyEvent {
  const at = Date.now();
  return anyEvent.parse({
    eventId: uuidv7(at),
    hlc: tick(at),
    deviceId: deviceId(),
    entityId,
    clientTs: new Date(at).toISOString(),
    type,
    payload,
  });
}

/** The fields a capture form may set, minus `name`, which is always required. */
export const OPTIONAL_ITEM_FIELDS = itemFieldNames.filter(
  (name): name is Exclude<ItemFieldName, 'name'> => name !== 'name',
);

export type ItemDraft = { name: string } & {
  [K in Exclude<ItemFieldName, 'name'>]?: ItemFieldValues[K];
};

/**
 * Creates an item.
 *
 * Fields left undefined are omitted rather than sent as null: "never set" and
 * "deliberately cleared" are different states, and only the second should write a
 * clock that a later edit has to beat.
 */
export function createItem(
  draft: ItemDraft,
  /**
   * Supplied when the id was decided before the item was saved.
   *
   * The capture form settles on one as soon as it opens, so that tags and
   * custom field values can be written against the item while it is still being
   * filled in. Left out, one is minted here as before.
   */
  itemId: string = uuidv7(),
): { itemId: string; events: AnyEvent[] } {
  const payload: Record<string, unknown> = { name: draft.name };
  for (const field of OPTIONAL_ITEM_FIELDS) {
    const value = draft[field];
    if (value !== undefined) payload[field] = value;
  }
  return { itemId, events: [envelope('item.created', itemId, payload)] };
}

/**
 * One event per changed field.
 *
 * Per-field rather than one event for the whole form, because per-field is the
 * granularity the merge works at (data model §3): two devices editing different
 * fields of the same item both win, and that only holds if the events are
 * separable in the first place.
 */
export function editItem<K extends Exclude<ItemFieldName, 'name'> | 'name'>(
  itemId: string,
  changes: Partial<{ [F in K]: ItemFieldValues[F] }>,
): AnyEvent[] {
  return Object.entries(changes)
    .filter(([, value]) => value !== undefined)
    .map(([field, value]) => envelope('item.fieldSet', itemId, { field, value }));
}

/** Soft, with a 30-day recovery window (FR-3.8). */
export const deleteItem = (itemId: string): AnyEvent[] => [envelope('item.deleted', itemId, {})];

export const restoreItem = (itemId: string): AnyEvent[] => [envelope('item.restored', itemId, {})];

export const setIntention = (
  itemId: string,
  intention: 'keep' | 'uncertain' | 'release' | 'invest',
): AnyEvent[] => [envelope('intention.set', itemId, { intention })];

// ---------------------------------------------------------------------------
// Tags (Epic 4)
// ---------------------------------------------------------------------------

/**
 * Applies a tag by name, creating it only if nothing already matches.
 *
 * The glossary is explicit that tags are "created in the flow of use, not
 * administered in advance", so typing a name is the whole interaction. The
 * reuse check is FR-4.14: without it "Camping" and "camping" become two tags
 * that split every count between them, and the split is invisible until someone
 * notices a report is wrong.
 */
export function applyTagByName(
  manifest: Manifest,
  itemId: string,
  displayName: string,
): { tagId: string; events: AnyEvent[] } {
  const trimmed = displayName.trim();
  const existing = findTagByName(manifest, trimmed);
  const events: AnyEvent[] = [];

  const tagId = existing?.tagId ?? uuidv7();
  if (existing === undefined) {
    events.push(
      envelope('tag.created', tagId, {
        // The casing typed is kept; the normalized form is what uniqueness uses.
        displayName: trimmed,
        normalizedName: normalizeTagName(trimmed),
      }),
    );
  }

  events.push(envelope('tag.applied', itemId, { itemId, tagId }));
  return { tagId, events };
}

export const removeTagFrom = (itemId: string, tagId: string): AnyEvent[] => [
  envelope('tag.removed', itemId, { itemId, tagId }),
];

/**
 * Changes a tag's display name, never its identity.
 *
 * The identity is what keeps "how many tee-shirts did I own last March"
 * answerable after the tag is renamed to "t-shirt" (FR-9.10).
 */
export const renameTag = (tagId: string, displayName: string): AnyEvent[] => [
  envelope('tag.renamed', tagId, {
    displayName: displayName.trim(),
    normalizedName: normalizeTagName(displayName),
  }),
];

export const deleteTag = (tagId: string): AnyEvent[] => [envelope('tag.deleted', tagId, {})];

/** FR-4.18: folds one tag into another, leaving the applications intact. */
export const mergeTagInto = (tagId: string, intoTagId: string): AnyEvent[] => [
  envelope('tag.merged', tagId, { intoTagId }),
];

// ---------------------------------------------------------------------------
// Categories and locations
// ---------------------------------------------------------------------------

/** Both are the same shape; only the event names and the depth limit differ. */
type Taxonomy = 'category' | 'location';

export function createTaxonomyNode(
  kind: Taxonomy,
  name: string,
  parentId?: string | null,
): { id: string; events: AnyEvent[] } {
  const id = uuidv7();
  return {
    id,
    events: [
      envelope(`${kind}.created`, id, {
        name: name.trim(),
        // Omitted rather than null when there is no parent: absent and
        // "deliberately detached" are different, and only the second is a write
        // a later edit has to beat.
        ...(parentId === undefined || parentId === null ? {} : { parentId }),
      }),
    ],
  };
}

export const renameTaxonomyNode = (kind: Taxonomy, id: string, name: string): AnyEvent[] => [
  envelope(`${kind}.updated`, id, { name: name.trim() }),
];

/** `null` detaches, making it a root. */
export const reparentTaxonomyNode = (
  kind: Taxonomy,
  id: string,
  parentId: string | null,
): AnyEvent[] => [envelope(`${kind}.updated`, id, { parentId })];

/**
 * Creates a category at a given id, for the first-run defaults (FR-4.1).
 *
 * Separate from `createTaxonomyNode` because that one mints an id, and the
 * defaults must not: their ids are fixed so that two devices seeding
 * independently produce one set of categories rather than two (see
 * `DEFAULT_CATEGORIES`).
 *
 * Deliberately not exposed as a general "create with this id" -- the only
 * legitimate caller is the seeder, and an arbitrary id from a form is how two
 * entities come to share one.
 */
export const seedCategory = (categoryId: string, name: string): AnyEvent[] => [
  envelope('category.created', categoryId, { name }),
];

export const deleteTaxonomyNode = (kind: Taxonomy, id: string): AnyEvent[] => [
  envelope(`${kind}.deleted`, id, {}),
];

// ---------------------------------------------------------------------------
// Custom fields (FR-4.4)
// ---------------------------------------------------------------------------

/**
 * Defines a field on a category.
 *
 * `options` is written only for the select types: carrying a list on a text
 * field would be meaningless state that a later edit still has to merge.
 */
export function defineField(params: {
  categoryId: string;
  name: string;
  type: CustomFieldType;
  options?: readonly string[];
}): { fieldId: string; events: AnyEvent[] } {
  const fieldId = uuidv7();
  const selectable = params.type === 'single-select' || params.type === 'multi-select';
  return {
    fieldId,
    events: [
      envelope('field.defined', fieldId, {
        categoryId: params.categoryId,
        name: params.name.trim(),
        type: params.type,
        ...(selectable && params.options !== undefined ? { options: [...params.options] } : {}),
      }),
    ],
  };
}

/**
 * `categoryId` travels with every edit, because the row's sort key is built from
 * it -- see the note on `field.updated` in the event schema.
 */
export const renameField = (fieldId: string, categoryId: string, name: string): AnyEvent[] => [
  envelope('field.updated', fieldId, { categoryId, name: name.trim() }),
];

export const setFieldOptions = (
  fieldId: string,
  categoryId: string,
  options: readonly string[],
): AnyEvent[] => [envelope('field.updated', fieldId, { categoryId, options: [...options] })];

export const deleteField = (fieldId: string, categoryId: string): AnyEvent[] => [
  envelope('field.deleted', fieldId, { categoryId }),
];

/** One answer on one item. `null` clears it, which is distinct from never answered. */
export const setFieldValue = (itemId: string, fieldId: string, value: FieldValue): AnyEvent[] => [
  envelope('item.customFieldSet', itemId, { fieldId, value }),
];

// ---------------------------------------------------------------------------
// Intention (Epic 5)
// ---------------------------------------------------------------------------

/**
 * Records a verdict, optionally with a reason and a date to reconsider on.
 *
 * Append-only: there is no event that edits or withdraws one, because FR-5.4
 * wants the whole history and "I keep deciding to keep this" is only visible if
 * nothing is overwritten.
 */
export const setIntentionWith = (
  itemId: string,
  intention: 'keep' | 'uncertain' | 'release' | 'invest',
  extras: { reason?: string; reviewAgainOn?: string } = {},
): AnyEvent[] => [
  envelope('intention.set', itemId, {
    intention,
    ...(extras.reason !== undefined && extras.reason.trim() !== ''
      ? { reason: extras.reason.trim() }
      : {}),
    ...(extras.reviewAgainOn !== undefined && extras.reviewAgainOn !== ''
      ? { reviewAgainOn: extras.reviewAgainOn }
      : {}),
  }),
];

// ---------------------------------------------------------------------------
// Release (Epic 7)
// ---------------------------------------------------------------------------

/**
 * Records how an item left (FR-7.2, FR-7.3).
 *
 * The existence of this outcome is what archives the item (FR-7.4), so there is
 * no separate "archive" event to keep in step with it.
 */
export const recordRelease = (
  itemId: string,
  outcome: ReleaseKind,
  onDate: string,
  extras: { recipient?: string; proceeds?: Money | null; note?: string } = {},
): AnyEvent[] => [
  envelope('release.recorded', itemId, {
    outcome,
    onDate,
    ...(extras.recipient !== undefined && extras.recipient.trim() !== ''
      ? { recipient: extras.recipient.trim() }
      : {}),
    ...(extras.proceeds !== undefined && extras.proceeds !== null
      ? { proceeds: extras.proceeds }
      : {}),
    ...(extras.note !== undefined && extras.note.trim() !== '' ? { note: extras.note.trim() } : {}),
  }),
];

/** FR-7.5: back to active. The outcome stays in the log, so nothing is forgotten. */
export const clearRelease = (itemId: string): AnyEvent[] => [
  envelope('release.cleared', itemId, {}),
];

// ---------------------------------------------------------------------------
// Investment entries (Epic 8)
// ---------------------------------------------------------------------------

export interface InvestmentDraft {
  name: string;
  notes?: string;
  categoryId?: string | null;
  referencePrice?: Money | null;
  priority?: number;
}

/**
 * Only the parts that were given are written.
 *
 * An omitted field and a field explicitly cleared are different writes: the
 * first leaves whatever another device may have set, the second beats it. A form
 * that sent every field on every save would silently undo the other device's
 * edits, so blanks are omitted here and clearing is its own explicit `null`.
 */
function investmentPayload(draft: Partial<InvestmentDraft>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (draft.name !== undefined && draft.name.trim() !== '') payload['name'] = draft.name.trim();
  if (draft.notes !== undefined && draft.notes.trim() !== '') payload['notes'] = draft.notes.trim();
  if (draft.categoryId !== undefined) payload['categoryId'] = draft.categoryId;
  if (draft.referencePrice !== undefined) payload['referencePrice'] = draft.referencePrice;
  if (draft.priority !== undefined) payload['priority'] = draft.priority;
  return payload;
}

/** FR-8.1. `replaces` is FR-8.2, recorded as its own event per item. */
export function createInvestment(
  draft: InvestmentDraft,
  replaces: readonly string[] = [],
): { investmentId: string; events: AnyEvent[] } {
  const investmentId = uuidv7();
  return {
    investmentId,
    events: [
      envelope('investment.created', investmentId, investmentPayload(draft)),
      ...replaces.flatMap((itemId) => linkInvestment(investmentId, itemId)),
    ],
  };
}

export const updateInvestment = (
  investmentId: string,
  changes: Partial<InvestmentDraft>,
): AnyEvent[] => [envelope('investment.updated', investmentId, investmentPayload(changes))];

/** FR-8.2: one event per item, so two devices linking different items both win. */
export const linkInvestment = (investmentId: string, itemId: string): AnyEvent[] => [
  envelope('investment.linked', investmentId, { itemId }),
];

export const unlinkInvestment = (investmentId: string, itemId: string): AnyEvent[] => [
  envelope('investment.unlinked', investmentId, { itemId }),
];

export const deleteInvestment = (investmentId: string): AnyEvent[] => [
  envelope('investment.deleted', investmentId, {}),
];

/**
 * The thing was bought (FR-8.4).
 *
 * Creates the catalog item in the same batch as the entry's closure, so the
 * `acquiredItemId` can never point at an item that does not exist -- the pair
 * either reaches the log together or not at all.
 *
 * Setting the replaced items to Release is deliberately *not* done here. FR-8.4
 * says the app offers it; a new coat does not oblige anyone to give away the old
 * one, and deciding that for them is exactly what NFR-SAFE-1 rules out.
 */
export function acquireInvestment(
  investmentId: string,
  draft: ItemDraft,
  acquiredOn: string,
): { itemId: string; events: AnyEvent[] } {
  const { itemId, events } = createItem(draft);
  return {
    itemId,
    events: [
      ...events,
      envelope('investment.acquired', investmentId, { acquiredItemId: itemId, acquiredOn }),
    ],
  };
}

/** FR-8.5. The reason is the point of recording it, but it is not compulsory. */
export const abandonInvestment = (
  investmentId: string,
  abandonedOn: string,
  reason?: string,
): AnyEvent[] => [
  envelope('investment.abandoned', investmentId, {
    abandonedOn,
    ...(reason !== undefined && reason.trim() !== '' ? { reason: reason.trim() } : {}),
  }),
];
